import { useMemo } from 'react';
import { usePathname } from 'expo-router';
import { usePlayer } from '@/contexts/PlayerContext';
import { useGlobalModalsState } from '@/hooks/useGlobalModalsState';

type ScreenName = 'home' | 'profile' | 'mylist' | 'foryou';

const SCREEN_PATH_PATTERNS: Record<ScreenName, string[]> = {
  home: ['/', '/(tabs)', '/(tabs)/index'],
  profile: ['/(tabs)/profile', '/(tabs)/profile_welcome_meta', '/profile_welcome_meta'],
  mylist: ['/(tabs)/mylist'],
  foryou: ['/(tabs)/foryou'],
};

interface ComputedVisibilityResult {
  isVisible: boolean;
}

export function useComputedScreenVisibility(screenName: ScreenName): ComputedVisibilityResult {
  const pathname = usePathname();
  const { isVisible: playerIsVisible, getLifecycleState } = usePlayer();
  const { hasGlobalModalVisible } = useGlobalModalsState();

  return useMemo(() => {
    const expectedPaths = SCREEN_PATH_PATTERNS[screenName];

    const pathMatches = expectedPaths.some(path => {
      if (path === '/') {
        return pathname === '/' || pathname === '';
      }
      return pathname === path || pathname.startsWith(path + '/');
    });

    const lifecycleState = getLifecycleState();
    const playerIsClosed = lifecycleState === 'CLOSED' && !playerIsVisible;

    const isVisible = pathMatches && playerIsClosed && !hasGlobalModalVisible;

    return { isVisible };
  }, [pathname, playerIsVisible, hasGlobalModalVisible, screenName, getLifecycleState]);
}

export function useHomeScreenVisibilityComputed(): ComputedVisibilityResult {
  return useComputedScreenVisibility('home');
}

export function useProfileScreenVisibilityComputed(): ComputedVisibilityResult {
  return useComputedScreenVisibility('profile');
}

export function useMyListScreenVisibilityComputed(): ComputedVisibilityResult {
  return useComputedScreenVisibility('mylist');
}

